// Pure ctx.http.get request-shaping + error-kind mapping. No DOM, no imports —
// unit-tested via node --test and consumed by api.ts under Vite. api.ts itself
// cannot be imported outside a browser (it instantiates DOM-touching host
// singletons — graphics/ui/icons/device — at module load), so the logic worth
// protecting with a fast regression test lives here instead.
// KEEP IN SYNC with ./net-http.d.mts, which hand-mirrors these signatures for
// the strict shell tsconfig (no allowJs).

/**
 * Reasons `/api/net/get` refuses a `useKey` call before it ever reaches the
 * upstream fetch (see `resolveNetCredential` / `apps/server/src/net/net-credential.mjs`).
 * All of these are POLICY denials, not upstream trouble, so `httpErrorKindFor`
 * maps every one of them to `kind: "denied"` regardless of which HTTP status
 * the route happened to answer with (`USEKEY_WITHOUT_APP` is 400, the rest are
 * 403) — an app branches on `reason`, not on the status, to tell "add your
 * key" (`NO_USER_KEY`) apart from "that host isn't allowed"
 * (`CREDENTIAL_HOST_NOT_GRANTED`).
 */
export const CREDENTIAL_DENIAL_REASONS = new Set([
  "USEKEY_WITHOUT_APP",
  "CREDENTIAL_NOT_GRANTED",
  "APP_NOT_INSTALLED",
  "CREDENTIAL_GRANT_INVALID",
  "CREDENTIAL_HOST_NOT_GRANTED",
  "GUEST_FORBIDDEN",
  "NO_USER_KEY",
]);

/**
 * Map the proxy's status (and, for credential denials, its reason) onto a
 * kind. The server already distinguishes these (403 denied / 429 limited /
 * 504 timeout / 502 upstream) precisely so a widget can tell "this host is
 * not allowed" apart from "try again in a minute".
 *
 * 403/"denied" means POLICY refused the request and retrying is pointless.
 * A response the proxy would not relay (BAD_CONTENT_TYPE, BAD_CONTENT_ENCODING,
 * RESPONSE_TOO_LARGE) arrives as 502/"upstream" instead — the caller was
 * allowed, the upstream just answered badly, and that is worth retrying.
 *
 * @param {number} status
 * @param {string} [reason]
 * @returns {"denied"|"rate-limited"|"timeout"|"upstream"|"network"}
 */
export function httpErrorKindFor(status, reason) {
  if (reason && CREDENTIAL_DENIAL_REASONS.has(reason)) return "denied";
  if (status === 429) return "rate-limited";
  if (status === 504 || status === 408) return "timeout";
  if (status === 401 || status === 403) return "denied";
  if (status === 0) return "network";
  return "upstream";
}

/**
 * The exact JSON body `POST /api/net/get` receives for a `ctx.http.get(url,
 * opts)` call.
 *
 * `appId` is a REQUIRED, SEPARATE parameter — never read out of `opts` — on
 * purpose: `createHttpApi(appId)` in api.ts closes over the calling module's
 * OWN id (the same value `ctx.analytics`/`ctx.logs`/`ctx.appState` use to
 * scope themselves) and this function has no way to see anything else, even
 * if a hostile module's `opts` object carries an `appId` property of its own
 * (the public `HttpApi.get` TYPE has no such field, but nothing stops a
 * caller who ignores the type from putting one in the object at runtime). A
 * module cannot borrow another app's credential grant by naming its id.
 *
 * @param {string} appId
 * @param {string} url
 * @param {{ timeoutMs?: number, useKey?: string }} [opts]
 * @returns {{ url: string, timeoutMs?: number, useKey?: string, appId: string }}
 */
export function buildNetGetBody(appId, url, opts) {
  return { url, timeoutMs: opts?.timeoutMs, useKey: opts?.useKey, appId };
}
